import { memo } from 'react';
import { cn } from '@/shared/utils/cn';
import { BarProgressProps, ProgressBarColor, ProgressBarSize } from '@/shared/types/progressbar';

/**
 * Mapeo de tamaños usando clases primitivas del sistema
 * Tokens: --ui-Positive-space_8, _10, _12, _16, _20
 */
const SIZE_CLASSES: Record<ProgressBarSize, string> = {
  xxs: 'ui-h-8', // 8px  → var(--ui-Positive-space_8)
  xs: 'ui-h-10', // 10px → var(--ui-Positive-space_10)
  sm: 'ui-h-12', // 12px → var(--ui-Positive-space_12)
  md: 'ui-h-16', // 16px → var(--ui-Positive-space_16)
  lg: 'ui-h-20', // 20px → var(--ui-Positive-space_20)
} as const;

/**
 * Colores del fill usando tokens semánticos (estado normal)
 */
const FILL_COLOR_CLASSES: Record<ProgressBarColor, string> = {
  brand: 'ui-bg-fill-brand',
  success: 'ui-bg-fill-success',
  danger: 'ui-bg-fill-danger',
  warning: 'ui-bg-fill-warning',
  info: 'ui-bg-fill-info',
  neutral: 'ui-bg-tertiary',
} as const;

/**
 * Colores del fill para estado seleccionado
 */
const FILL_COLOR_SELECTED_CLASSES: Record<ProgressBarColor, string> = {
  brand: 'ui-bg-fill-brand-selected',
  success: 'ui-bg-fill-success-selected',
  danger: 'ui-bg-fill-danger-selected',
  warning: 'ui-bg-fill-warning-selected',
  info: 'ui-bg-fill-info-selected',
  neutral: 'ui-bg-fill-tertiary-selected',
} as const;

export interface BarProgressExtendedProps extends BarProgressProps {
  /** Estado de selección (para uso en tablas) */
  selected?: boolean;
}

/**
 * Componente _bar - Barra de progreso interna
 *
 * @example
 * <BarProgress progress={75} size="sm" color="brand" />
 *
 * @example
 * // En estado seleccionado (para tablas)
 * <BarProgress progress={75} size="xxs" color="brand" selected />
 */
export const BarProgress = memo(
  ({
    progress = 0,
    size = 'xxs',
    color = 'brand',
    duration = 0,
    withBackground = true,
    selected = false,
    className,
  }: BarProgressExtendedProps) => {
    // Clamp progress entre 0 y 100
    const clampedProgress = Math.min(Math.max(progress, 0), 100);

    // Clases del contenedor
    const containerClasses = cn(
      'ui-w-full ui-overflow-hidden ui-rounded-control-pill',
      SIZE_CLASSES[size],
      withBackground && (selected ? 'ui-bg-fill-tertiary-selected' : 'ui-bg-fill-tertiary'),
      !withBackground && 'ui-bg-transparent',
      className,
    );

    // Clases del fill - solo aplicar transición si duration > 0
    const fillClasses = cn(
      'ui-h-full ui-rounded-control-pill',
      duration > 0 && 'ui-transition-all',
      selected ? FILL_COLOR_SELECTED_CLASSES[color] : FILL_COLOR_CLASSES[color],
    );

    // Estilos dinámicos
    const fillStyle: React.CSSProperties = {
      width: `${clampedProgress}%`,
      // Si duration > 0, usar transición CSS; si duration === 0, desactivar explícitamente
      transition: duration > 0 ? `width ${duration}ms ease-out` : 'none',
    };

    return (
      <div className={containerClasses}>
        <div
          className={fillClasses}
          style={fillStyle}
          role="progressbar"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    );
  },
);

BarProgress.displayName = 'BarProgress';
